import { useEffect, useState, Fragment } from 'react';
import { BarChart3, Download, TrendingUp, Wallet, Truck, MapPin, Package, ShoppingBag, Percent, CreditCard, Coins, Eye, EyeOff, Loader2, X, HandCoins, Info, PiggyBank, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { adminAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { ReportCharts } from '@/components/admin/ReportCharts';

const money = (n) => 'R' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Small info-icon tooltip explaining what a report field is composed of.
// Works on hover (desktop) and focus/tap (mobile) via the Radix trigger.
function FieldHelp({ text, side = 'top' }) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid="field-help"
            aria-label="What this value is composed of"
            className="inline-flex align-middle ml-1 text-foreground/40 hover:text-teal focus:text-teal focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          >
            <Info className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-[260px] whitespace-normal text-left font-body leading-relaxed normal-case tracking-normal">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const monthLabel = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('en-ZA', { month: 'short', year: 'numeric' });
};

const PAY_TYPES = [
  { key: 'all', label: 'All' },
  { key: 'card', label: 'Card' },
  { key: 'eft', label: 'EFT' },
];

const CHART_TOGGLES = [
  { key: 'trend', label: 'Income vs Profit' },
  { key: 'composition', label: 'Income Composition' },
  { key: 'paytype', label: 'Payment Split' },
  { key: 'supplier', label: 'Profit by Supplier' },
];

export default function AdminReports({ mode = 'sales' }) {
  const isSales = mode !== 'supplier';
  const isSupplier = mode === 'supplier';
  const [months, setMonths] = useState([]);
  const [selected, setSelected] = useState([]);
  const [paymentType, setPaymentType] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingSection, setExportingSection] = useState(null);
  const [chartsVisible, setChartsVisible] = useState({ trend: true, composition: true, paytype: false, supplier: true });
  const [drillOrder, setDrillOrder] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [poRecon, setPoRecon] = useState(null);
  const [poReconExporting, setPoReconExporting] = useState(false);
  const [uncoveredExporting, setUncoveredExporting] = useState(false);
  const [reconExpanded, setReconExpanded] = useState(() => new Set());
  const toggleRecon = (i) => setReconExpanded((prev) => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  const [commission, setCommission] = useState(null);
  const [commissionExporting, setCommissionExporting] = useState(false);

  const openDrill = async (orderId) => {
    setDrillLoading(true);
    setDrillOrder({ loading: true });
    try {
      const res = await adminAPI.getReportOrderDetail(orderId);
      setDrillOrder(res.data);
    } catch {
      toast.error('Failed to load order detail');
      setDrillOrder(null);
    } finally {
      setDrillLoading(false);
    }
  };

  // Load the list of months that have paid orders, default to the most recent.
  useEffect(() => {
    let active = true;
    adminAPI.getReportMonths()
      .then(r => {
        if (!active) return;
        const ms = r.data.months || [];
        setMonths(ms);
        setSelected(ms.length ? [ms[0]] : []);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  // Re-fetch the report whenever the selection or payment-type filter changes.
  useEffect(() => {
    let active = true;
    adminAPI.getFinancialReport({ months: selected.join(','), payment_type: paymentType })
      .then(r => { if (active) setData(r.data); })
      .catch(() => { if (active) setData(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [selected, paymentType]);

  // PO reconciliation summary follows the same month selection (payment-type independent).
  useEffect(() => {
    let active = true;
    adminAPI.getPOReconciliationSummary({ months: selected.join(',') })
      .then(r => { if (active) setPoRecon(r.data); })
      .catch(() => { if (active) setPoRecon(null); });
    return () => { active = false; };
  }, [selected]);

  // Collection commission report follows the same month selection.
  useEffect(() => {
    let active = true;
    adminAPI.getCollectionCommission({ months: selected.join(',') })
      .then(r => { if (active) setCommission(r.data); })
      .catch(() => { if (active) setCommission(null); });
    return () => { active = false; };
  }, [selected]);

  const handleExportCommission = async () => {
    setCommissionExporting(true);
    try {
      const res = await adminAPI.exportCollectionCommission({ months: selected.join(',') });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Collection_Commission_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Collection commission exported');
    } catch { toast.error('Export failed'); }
    finally { setCommissionExporting(false); }
  };

  const handleExportPORecon = async () => {
    setPoReconExporting(true);
    try {
      const res = await adminAPI.exportPOReconciliationSummary({ months: selected.join(',') });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_PO_Reconciliation_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PO reconciliation exported');
    } catch { toast.error('Export failed'); }
    finally { setPoReconExporting(false); }
  };

  const handleExportUncovered = async () => {
    setUncoveredExporting(true);
    try {
      const res = await adminAPI.exportUncoveredOrders({ months: selected.join(',') });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Not_Yet_On_PO_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Not-yet-on-PO orders exported');
    } catch { toast.error('Export failed'); }
    finally { setUncoveredExporting(false); }
  };

  const toggleMonth = (m) => {
    setSelected(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  const handleExport = async (section = 'all') => {
    setExportingSection(section);
    setExporting(true);
    try {
      const res = await adminAPI.exportFinancialReport({ months: selected.join(','), payment_type: paymentType, section });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const suffix = section === 'all' ? '' : `_${section}`;
      a.download = `AVZ_Financial_Report${suffix}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report exported');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); setExportingSection(null); }
  };

  const s = data?.summary;
  const orders = data?.orders || [];
  const byMonth = data?.by_month || [];
  const bySupplier = data?.by_supplier || [];
  const netBySupplier = data?.net_by_supplier || [];

  const summaryCards = [
    { key: 'revenue', label: 'Revenue (Goods)', value: money(s?.revenue), icon: TrendingUp, color: 'text-gold', bg: 'bg-gold/15', help: 'Sum of goods selling price × quantity across all paid orders. Excludes courier and collection fees.' },
    { key: 'total-income', label: 'Total Income', value: money(s?.total_income), sub: 'Goods + Courier + Collection', icon: Coins, color: 'text-gold', bg: 'bg-gold/15', help: 'Revenue (goods) + courier fees + collection fees collected from customers.' },
    { key: 'cogs', label: 'Cost of Goods', value: money(s?.cogs), icon: Package, color: 'text-orange-400', bg: 'bg-orange-400/15', help: 'Sum of supplier cost price × quantity for all items sold (cost captured at time of sale).' },
    { key: 'gross-profit', label: 'Gross Profit', value: money(s?.gross_profit), sub: `${s?.margin || 0}% margin`, icon: Wallet, color: 'text-teal', bg: 'bg-teal/15', help: 'Total Income − Cost of Goods − Collection Commission. Unlike the per-supplier Profit, this includes courier & collection fees.' },
    { key: 'tithe-payable', label: 'Tithe Payable', value: money(s?.tithe_total), sub: `${s?.tithe_percentage ?? 10}% of net profit`, icon: PiggyBank, color: 'text-gold', bg: 'bg-gold/15', help: `${s?.tithe_percentage ?? 10}% of total Net Profit for the period. Net Profit = (Sales + Courier + Collection) − Cost − Commission. Change the rate in Admin → Settings.` },
    { key: 'courier', label: 'Courier Fees', value: money(s?.courier_fee), icon: Truck, color: 'text-foreground/70', bg: 'bg-muted', help: 'Total delivery / courier charged to customers across paid orders.' },
    { key: 'collection', label: 'Collection Fees', value: money(s?.collection_fee), icon: MapPin, color: 'text-foreground/70', bg: 'bg-muted', help: 'Total collection-point fees charged to customers across paid orders.' },
    { key: 'collection-commission', label: 'Collection Commission', value: money(s?.collection_commission), sub: 'Paid to collection points', icon: HandCoins, color: 'text-orange-400', bg: 'bg-orange-400/15', help: 'Flat commission payable to collection points — one per paid order, even if the order spans multiple suppliers.' },
    { key: 'orders', label: 'Paid Orders', value: s?.order_count ?? 0, icon: ShoppingBag, color: 'text-teal', bg: 'bg-teal/15' },
    { key: 'aov', label: 'Avg Order Value', value: money(s?.average_order_value), icon: Percent, color: 'text-gold', bg: 'bg-gold/15', help: 'Total Income ÷ number of paid orders.' },
  ];

  const tithePct = s?.tithe_percentage ?? 10;
  const supplierTotals = bySupplier.reduce((a, sup) => ({
    sales: a.sales + (sup.sales || 0),
    cost: a.cost + (sup.cost || 0),
    cost_ex_vat: a.cost_ex_vat + (sup.cost_ex_vat ?? sup.cost ?? 0),
    profit: a.profit + (sup.profit || 0),
    units: a.units + (sup.units || 0),
    orders: a.orders + (sup.order_count || 0),
  }), { sales: 0, cost: 0, cost_ex_vat: 0, profit: 0, units: 0, orders: 0 });

  const netTotals = netBySupplier.reduce((a, sup) => ({
    total_income: a.total_income + (sup.total_income || 0),
    cost: a.cost + (sup.cost || 0),
    cost_ex_vat: a.cost_ex_vat + (sup.cost_ex_vat ?? sup.cost ?? 0),
    gross_profit: a.gross_profit + (sup.gross_profit || 0),
    commission: a.commission + (sup.commission || 0),
    net_profit: a.net_profit + (sup.net_profit || 0),
    tithe: a.tithe + (sup.tithe || 0),
    orders: a.orders + (sup.order_count || 0),
  }), { total_income: 0, cost: 0, cost_ex_vat: 0, gross_profit: 0, commission: 0, net_profit: 0, tithe: 0, orders: 0 });

  return (
    <div data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-vintage/15"><BarChart3 className="h-6 w-6 text-vintage" /></div>
          <div>
            <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">
              {isSupplier ? 'Supplier Reports' : 'Sales Reports'}
            </h1>
            <p className="font-body text-sm text-foreground/70 mt-0.5">
              {isSupplier
                ? 'Per-supplier profitability & Purchase Order coverage — paid orders only'
                : 'Monthly sales performance — paid orders only'}
            </p>
          </div>
        </div>
        <Button
          data-testid="report-export-btn"
          onClick={() => handleExport('all')}
          disabled={exporting || orders.length === 0}
          className="rounded-full bg-teal-dark border border-transparent text-white hover:bg-teal font-body"
        >
          <Download className="h-4 w-4 mr-2" /> {exportingSection === 'all' ? 'Exporting…' : 'Export All to Excel'}
        </Button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl bg-card border border-border p-5 mb-6 space-y-4">
        <div>
          <p className="font-body text-xs uppercase tracking-[0.15em] text-foreground/60 mb-2">Period (select one or more)</p>
          <div className="flex flex-wrap gap-2" data-testid="report-month-selector">
            {months.length === 0 && <span className="font-body text-sm text-foreground/40">No paid orders yet</span>}
            {months.map(m => {
              const on = selected.includes(m);
              return (
                <button
                  key={m}
                  type="button"
                  data-testid={`report-month-${m}`}
                  onClick={() => toggleMonth(m)}
                  className={`px-3.5 py-1.5 rounded-full font-body text-sm border transition-all ${
                    on ? 'bg-vintage text-white border-vintage' : 'bg-transparent text-foreground/70 border-border hover:border-vintage/50'
                  }`}
                >
                  {monthLabel(m)}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <p className="font-body text-xs uppercase tracking-[0.15em] text-foreground/60 mb-2">Payment Type</p>
          <div className="inline-flex rounded-full bg-muted p-1" data-testid="report-paytype-selector">
            {PAY_TYPES.map(pt => (
              <button
                key={pt.key}
                type="button"
                data-testid={`report-paytype-${pt.key}`}
                onClick={() => setPaymentType(pt.key)}
                className={`px-4 py-1.5 rounded-full font-body text-sm transition-all ${
                  paymentType === pt.key ? 'bg-vintage text-white' : 'text-foreground/60 hover:text-foreground'
                }`}
              >
                {pt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-28 bg-muted rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* Summary cards */}
          {isSales && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-8">
            {summaryCards.map(c => (
              <div key={c.key} data-testid={`report-summary-${c.key}`} className="p-5 rounded-2xl bg-card border border-border">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-body text-xs uppercase tracking-[0.12em] text-foreground/60 inline-flex items-center">
                    {c.label}{c.help && <FieldHelp text={c.help} />}
                  </span>
                  <div className={`p-1.5 rounded-lg ${c.bg}`}><c.icon className={`h-4 w-4 ${c.color}`} /></div>
                </div>
                <p className="font-body text-2xl font-bold text-foreground" data-testid="report-stat-value">{c.value}</p>
                {c.sub && <p className="font-body text-xs text-foreground/50 mt-1">{c.sub}</p>}
              </div>
            ))}
          </div>
          )}

          {/* Visualizations — toggleable, directly below the status cards */}
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="chart-toggle-bar">
            <span className="font-body text-xs uppercase tracking-[0.12em] text-foreground/50 mr-1">Charts</span>
            {CHART_TOGGLES.filter(t => isSupplier ? t.key === 'supplier' : t.key !== 'supplier').map(t => {
              const on = chartsVisible[t.key];
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`chart-toggle-${t.key}`}
                  onClick={() => setChartsVisible(v => ({ ...v, [t.key]: !v[t.key] }))}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-body text-xs border transition-all ${
                    on ? 'bg-teal/15 text-teal border-teal/40' : 'bg-transparent text-foreground/50 border-border hover:border-teal/30'
                  }`}
                >
                  {on ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  {t.label}
                </button>
              );
            })}
          </div>

          <ReportCharts
            byMonth={byMonth}
            summary={s || {}}
            orders={orders}
            bySupplier={bySupplier}
            visible={isSupplier
              ? { trend: false, composition: false, paytype: false, supplier: chartsVisible.supplier }
              : { ...chartsVisible, supplier: false }}
          />

          {/* Per-month breakdown */}
          {isSales && byMonth.length > 1 && (
            <div className="rounded-2xl bg-card border border-border p-6 mb-8 overflow-x-auto">
              <div className="flex items-center justify-between mb-4 gap-3">
                <h2 className="font-heading text-lg font-medium text-foreground">By Month</h2>
                <Button
                  data-testid="bymonth-export-btn"
                  onClick={() => handleExport('by_month')}
                  disabled={exporting}
                  variant="outline"
                  size="sm"
                  className="rounded-full font-body text-xs gap-1.5 shrink-0"
                >
                  <Download className="h-3.5 w-3.5" /> {exportingSection === 'by_month' ? 'Exporting…' : 'Export'}
                </Button>
              </div>
              <table className="w-full" data-testid="report-bymonth-table">
                <thead>
                  <tr className="border-b border-border text-left font-body text-xs uppercase tracking-wider text-foreground/60">
                    <th className="pb-3">Month</th>
                    <th className="pb-3 text-right">Revenue<FieldHelp text="Goods selling price × quantity for orders in this month (excludes courier & collection fees)." /></th>
                    <th className="pb-3 text-right">COGS<FieldHelp text="Cost of Goods Sold — supplier cost price × quantity for items sold this month." /></th>
                    <th className="pb-3 text-right">COGS Ex VAT<FieldHelp text="VAT-exclusive Cost of Goods Sold — for VAT-vendor suppliers the VAT is stripped from the cost; non-VAT items are unchanged." /></th>
                    <th className="pb-3 text-right">Commission<FieldHelp text="Collection commission payable this month — flat fee per paid order to the collection point." /></th>
                    <th className="pb-3 text-right">Gross Profit<FieldHelp text="Total Income − COGS − Collection Commission for this month (includes courier & collection fees)." /></th>
                    <th className="pb-3 text-right">Courier<FieldHelp text="Courier / delivery fees collected from customers this month." /></th>
                    <th className="pb-3 text-right">Collection<FieldHelp text="Collection-point fees collected from customers this month." /></th>
                    <th className="pb-3 text-right">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {byMonth.map(b => (
                    <tr key={b.month} data-testid={`report-bymonth-row-${b.month}`} className="border-b border-border/50 last:border-0 font-body text-sm">
                      <td className="py-2.5 text-foreground">{monthLabel(b.month)}</td>
                      <td className="py-2.5 text-right text-foreground">{money(b.revenue)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(b.cogs)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(b.cogs_ex_vat ?? b.cogs)}</td>
                      <td className="py-2.5 text-right text-orange-400">{money(b.collection_commission)}</td>
                      <td className="py-2.5 text-right font-semibold text-teal">{money(b.gross_profit)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(b.courier_fee)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(b.collection_fee)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{b.order_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By Supplier breakdown */}
          {isSupplier && bySupplier.length > 0 && (
            <div className="rounded-2xl bg-card border border-border p-6 mb-8 overflow-x-auto" data-testid="report-bysupplier">
              <div className="flex items-center justify-between mb-4 gap-3">
                <h2 className="font-heading text-lg font-medium text-foreground">Profit Margin by Supplier</h2>
                <Button
                  data-testid="bysupplier-export-btn"
                  onClick={() => handleExport('by_supplier')}
                  disabled={exporting}
                  variant="outline"
                  size="sm"
                  className="rounded-full font-body text-xs gap-1.5 shrink-0"
                >
                  <Download className="h-3.5 w-3.5" /> {exportingSection === 'by_supplier' ? 'Exporting…' : 'Export'}
                </Button>
              </div>
              <table className="w-full" data-testid="report-bysupplier-table">
                <thead>
                  <tr className="border-b border-border text-left font-body text-xs uppercase tracking-wider text-foreground/60">
                    <th className="pb-3">Supplier</th>
                    <th className="pb-3 text-right">Sales<FieldHelp text="Selling price × quantity for this supplier's items. Excludes courier & collection fees (those are order-level)." /></th>
                    <th className="pb-3 text-right">Cost<FieldHelp text="Supplier cost price × quantity, captured on each order line at time of sale." /></th>
                    <th className="pb-3 text-right">Cost Ex VAT<FieldHelp text="VAT-exclusive cost — for VAT-vendor suppliers the VAT is stripped out; non-VAT items match the Cost column." /></th>
                    <th className="pb-3 text-right">Profit<FieldHelp text="Sales − Cost. The goods-level markup earned on this supplier's products." /></th>
                    <th className="pb-3 text-right">Margin<FieldHelp text="Profit ÷ Sales × 100." /></th>
                    <th className="pb-3 text-right">Units</th>
                    <th className="pb-3 text-right">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {bySupplier.map(sup => (
                    <tr key={sup.supplier_id} data-testid={`report-supplier-row-${sup.supplier_id}`} className="border-b border-border/50 last:border-0 font-body text-sm">
                      <td className="py-2.5 text-foreground">{sup.supplier_name}</td>
                      <td className="py-2.5 text-right text-foreground">{money(sup.sales)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(sup.cost)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(sup.cost_ex_vat ?? sup.cost)}</td>
                      <td className="py-2.5 text-right font-semibold text-teal">{money(sup.profit)}</td>
                      <td className="py-2.5 text-right">
                        <span className={`font-medium ${sup.margin >= 0 ? 'text-teal' : 'text-orange-400'}`}>{sup.margin}%</span>
                      </td>
                      <td className="py-2.5 text-right text-foreground/70">{sup.units}</td>
                      <td className="py-2.5 text-right text-foreground/70">{sup.order_count}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border font-body text-sm font-semibold" data-testid="report-supplier-totals">
                    <td className="pt-3 text-foreground">Total</td>
                    <td className="pt-3 text-right text-foreground">{money(supplierTotals.sales)}</td>
                    <td className="pt-3 text-right text-foreground/70">{money(supplierTotals.cost)}</td>
                    <td className="pt-3 text-right text-foreground/70">{money(supplierTotals.cost_ex_vat)}</td>
                    <td className="pt-3 text-right text-teal">{money(supplierTotals.profit)}</td>
                    <td className="pt-3"></td>
                    <td className="pt-3 text-right text-foreground/70">{supplierTotals.units}</td>
                    <td className="pt-3 text-right text-foreground/70">{supplierTotals.orders}</td>
                  </tr>
                </tfoot>
              </table>
              <p className="font-body text-xs text-foreground/40 mt-3">Goods-level margin (courier &amp; collection fees are order-level and excluded here).</p>
            </div>
          )}

          {/* Net Profit Margin by Supplier — fees & commission allocated, tithe applied */}
          {isSupplier && netBySupplier.length > 0 && (
            <div className="rounded-2xl bg-card border border-border p-6 mb-8 overflow-x-auto" data-testid="report-net-bysupplier">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                  <h2 className="font-heading text-lg font-medium text-foreground">Net Profit Margin by Supplier</h2>
                  <p className="font-body text-xs text-foreground/50 mt-0.5">Courier, collection fees &amp; commission allocated to each supplier by their share of the order. Tithe = {tithePct}% of Net Profit.</p>
                </div>
                <Button
                  data-testid="net-bysupplier-export-btn"
                  onClick={() => handleExport('net_by_supplier')}
                  disabled={exporting}
                  variant="outline"
                  size="sm"
                  className="rounded-full font-body text-xs gap-1.5 shrink-0"
                >
                  <Download className="h-3.5 w-3.5" /> {exportingSection === 'net_by_supplier' ? 'Exporting…' : 'Export'}
                </Button>
              </div>
              <table className="w-full" data-testid="report-net-bysupplier-table">
                <thead>
                  <tr className="border-b border-border text-left font-body text-xs uppercase tracking-wider text-foreground/60">
                    <th className="pb-3">Supplier</th>
                    <th className="pb-3 text-right">Total Income<FieldHelp text="This supplier's Sales + allocated Courier + allocated Collection fees (fees split by the supplier's share of each order's value)." /></th>
                    <th className="pb-3 text-right">Cost<FieldHelp text="Supplier cost price × quantity for this supplier's sold items." /></th>
                    <th className="pb-3 text-right">Cost Ex VAT<FieldHelp text="VAT-exclusive cost — for VAT-vendor suppliers the VAT is stripped out; non-VAT items match the Cost column." /></th>
                    <th className="pb-3 text-right">Gross Profit<FieldHelp text="Total Income − Cost (before commission is deducted)." /></th>
                    <th className="pb-3 text-right">Commission<FieldHelp text="Collection commission allocated to this supplier by its share of each order's value." /></th>
                    <th className="pb-3 text-right">Net Profit<FieldHelp text="Total Income − Cost − Commission for this supplier." /></th>
                    <th className="pb-3 text-right">Net Margin<FieldHelp text="Net Profit ÷ Total Income × 100." /></th>
                    <th className="pb-3 text-right">Tithe ({tithePct}%)<FieldHelp text={`${tithePct}% × Net Profit. Configurable in Admin → Settings → Tithe Percentage.`} /></th>
                    <th className="pb-3 text-right">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {netBySupplier.map(sup => (
                    <tr key={sup.supplier_id} data-testid={`report-net-supplier-row-${sup.supplier_id}`} className="border-b border-border/50 last:border-0 font-body text-sm">
                      <td className="py-2.5 text-foreground">{sup.supplier_name}</td>
                      <td className="py-2.5 text-right text-foreground">{money(sup.total_income)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(sup.cost)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(sup.cost_ex_vat ?? sup.cost)}</td>
                      <td className="py-2.5 text-right text-foreground">{money(sup.gross_profit)}</td>
                      <td className="py-2.5 text-right text-orange-400">{money(sup.commission)}</td>
                      <td className="py-2.5 text-right font-semibold text-teal">{money(sup.net_profit)}</td>
                      <td className="py-2.5 text-right">
                        <span className={`font-medium ${sup.net_margin >= 0 ? 'text-teal' : 'text-orange-400'}`}>{sup.net_margin}%</span>
                      </td>
                      <td className="py-2.5 text-right font-semibold text-gold" data-testid={`report-net-supplier-tithe-${sup.supplier_id}`}>{money(sup.tithe)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{sup.order_count}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border font-body text-sm font-semibold" data-testid="report-net-supplier-totals">
                    <td className="pt-3 text-foreground">Total</td>
                    <td className="pt-3 text-right text-foreground">{money(netTotals.total_income)}</td>
                    <td className="pt-3 text-right text-foreground/70">{money(netTotals.cost)}</td>
                    <td className="pt-3 text-right text-foreground/70">{money(netTotals.cost_ex_vat)}</td>
                    <td className="pt-3 text-right text-foreground">{money(netTotals.gross_profit)}</td>
                    <td className="pt-3 text-right text-orange-400">{money(netTotals.commission)}</td>
                    <td className="pt-3 text-right text-teal">{money(netTotals.net_profit)}</td>
                    <td className="pt-3"></td>
                    <td className="pt-3 text-right text-gold" data-testid="report-net-supplier-tithe-total">{money(netTotals.tithe)}</td>
                    <td className="pt-3 text-right text-foreground/70">{netTotals.orders}</td>
                  </tr>
                </tfoot>
              </table>
              <p className="font-body text-xs text-foreground/40 mt-3">Net Profit = (Sales + Courier + Collection) − Cost − Commission. Tithe = {tithePct}% of Net Profit — change it in Admin → Settings.</p>
            </div>
          )}

          {/* PO Reconciliation — sent POs vs order records, per vendor per month */}
          {isSupplier && poRecon && poRecon.rows && poRecon.rows.length > 0 && (
            <div className="rounded-2xl bg-card border border-border p-6 mb-8 overflow-x-auto" data-testid="report-po-reconciliation">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                  <h2 className="font-heading text-lg font-medium text-foreground">PO Reconciliation</h2>
                  <p className="font-body text-xs text-foreground/50 mt-0.5">Supplier cost from paid order records (captured cost, excl. refunds) vs what's covered by a sent PO.</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    data-testid="po-uncovered-export-btn"
                    onClick={handleExportUncovered}
                    disabled={uncoveredExporting}
                    variant="outline"
                    className="rounded-full border-orange-400/40 text-orange-300 hover:bg-orange-400/10 font-body text-sm gap-2"
                    title="Export a finance-ready list of every paid order not yet on a PO"
                  >
                    {uncoveredExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Export not-yet-on-PO
                  </Button>
                  <Button
                    data-testid="po-recon-export-btn"
                    onClick={handleExportPORecon}
                    disabled={poReconExporting}
                    variant="outline"
                    className="rounded-full border-teal/40 text-teal hover:bg-teal/10 font-body text-sm gap-2"
                  >
                    {poReconExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Export
                  </Button>
                </div>
              </div>
              <table className="w-full" data-testid="report-po-recon-table">
                <thead>
                  <tr className="border-b border-border text-left font-body text-xs uppercase tracking-wider text-foreground/60">
                    <th className="pb-3">Month</th>
                    <th className="pb-3">Supplier</th>
                    <th className="pb-3 text-right">Orders</th>
                    <th className="pb-3 text-right">Order Cost (records)<FieldHelp text="Captured supplier cost from paid order records (excludes refunds) for this vendor & month. Matches the Cost column in Profit Margin by Supplier." /></th>
                    <th className="pb-3 text-right">Covered by PO<FieldHelp text="Portion of that order cost already included on a sent Purchase Order for this vendor." /></th>
                    <th className="pb-3 text-right">Not Yet on PO<FieldHelp text="Paid-order cost not yet covered by any sent PO for this vendor (Order Cost − Covered by PO). Click the amount to see exactly which orders are still missing from a PO." /></th>
                    <th className="pb-3">Purchase Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {poRecon.rows.map((r, i) => {
                    const uncovered = r.uncovered_orders || [];
                    const canExpand = uncovered.length > 0;
                    const isOpen = reconExpanded.has(i);
                    return (
                      <Fragment key={i}>
                        <tr data-testid={`po-recon-row-${i}`} className="border-b border-border/50 last:border-0 font-body text-sm">
                          <td className="py-2.5 text-foreground/70 text-xs">{monthLabel(r.month)}</td>
                          <td className="py-2.5 text-foreground">{r.supplier_name}</td>
                          <td className="py-2.5 text-right text-foreground/70">{r.order_count}</td>
                          <td className="py-2.5 text-right text-foreground">{money(r.order_cost)}</td>
                          <td className="py-2.5 text-right text-teal">{money(r.po_covered_cost)}</td>
                          <td className="py-2.5 text-right">
                            {canExpand ? (
                              <button
                                type="button"
                                data-testid={`po-recon-expand-${i}`}
                                onClick={() => toggleRecon(i)}
                                className="inline-flex items-center gap-1 font-medium text-orange-400 hover:text-orange-300 focus:outline-none"
                                title="Show the exact orders not yet on a PO"
                              >
                                {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                {money(r.not_po_cost)}
                              </button>
                            ) : (
                              <span className="text-foreground/40">{money(r.not_po_cost)}</span>
                            )}
                          </td>
                          <td className="py-2.5 text-foreground/60 text-xs">{(r.po_numbers || []).join(', ') || '—'}</td>
                        </tr>
                        {isOpen && canExpand && (
                          <tr data-testid={`po-recon-detail-${i}`} className="border-b border-border/50 bg-orange-500/5">
                            <td colSpan={7} className="px-3 py-3">
                              <p className="font-body text-[11px] uppercase tracking-wider text-orange-300/80 mb-2">
                                {uncovered.length} order{uncovered.length === 1 ? '' : 's'} not yet on a PO — {r.supplier_name}
                              </p>
                              <div className="space-y-1">
                                {uncovered.map((o) => (
                                  <div key={o.order_id} data-testid={`po-recon-uncovered-${o.order_number}`} className="flex items-center justify-between gap-3 font-body text-xs text-foreground/80 py-1 border-b border-border/30 last:border-0">
                                    <span className="font-semibold text-foreground">{o.order_number}</span>
                                    <span className="flex-1 truncate text-foreground/60">{o.customer}</span>
                                    <span className="text-foreground/50">{(o.created_at || '').slice(0, 10)}</span>
                                    <span className="font-semibold text-orange-300 whitespace-nowrap">{money(o.value)}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border font-body text-sm font-semibold">
                    <td className="pt-3" colSpan={3}>Total</td>
                    <td className="pt-3 text-right text-foreground">{money(poRecon.totals?.order_cost)}</td>
                    <td className="pt-3 text-right text-teal">{money(poRecon.totals?.po_covered_cost)}</td>
                    <td className="pt-3 text-right text-orange-400">{money(poRecon.totals?.not_po_cost)}</td>
                    <td className="pt-3"></td>
                  </tr>
                </tfoot>
              </table>
              <p className="font-body text-xs text-foreground/40 mt-3">"Order Cost (records)" matches the Cost column in Profit Margin by Supplier. "Not Yet on PO" = paid orders not yet covered by a sent PO for that vendor.</p>
            </div>
          )}

          {/* Collection Commission — per month → per location, split per supplier */}
          {isSupplier && commission && commission.months && commission.months.length > 0 && (
            <div className="rounded-2xl bg-card border border-border p-6 mb-8 overflow-x-auto" data-testid="report-collection-commission">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                  <h2 className="font-heading text-lg font-medium text-foreground">Collection Commission Payable</h2>
                  <p className="font-body text-xs text-foreground/50 mt-0.5">Per month → per collection location. Flat commission per paid order (shown once, listed per supplier).</p>
                </div>
                <Button
                  data-testid="commission-export-btn"
                  onClick={handleExportCommission}
                  disabled={commissionExporting}
                  variant="outline"
                  className="rounded-full border-teal/40 text-teal hover:bg-teal/10 font-body text-sm gap-2 flex-shrink-0"
                >
                  {commissionExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Export
                </Button>
              </div>

              <div className="space-y-6">
                {commission.months.map((mm) => (
                  <div key={mm.month} data-testid={`commission-month-${mm.month}`}>
                    <div className="flex items-center justify-between bg-charcoal/90 text-white rounded-t-xl px-4 py-2">
                      <span className="font-heading text-base">{monthLabel(mm.month)}</span>
                      <span className="font-body text-sm">{mm.order_count} orders · {money(mm.total_commission)}</span>
                    </div>
                    <div className="border border-border border-t-0 rounded-b-xl divide-y divide-border">
                      {mm.locations.map((L) => (
                        <div key={L.location} className="p-4" data-testid="commission-location">
                          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                            <span className="font-body text-sm font-bold text-foreground inline-flex items-center gap-1.5">
                              <MapPin className="h-4 w-4 text-teal" /> {L.location}
                            </span>
                            <span className="font-body text-xs text-foreground/60">{L.order_count} orders · <span className="text-teal font-semibold">{money(L.total_commission)}</span></span>
                          </div>
                          <table className="w-full">
                            <thead>
                              <tr className="text-left font-body text-[11px] uppercase tracking-wider text-foreground/50">
                                <th className="pb-1.5">Order #</th>
                                <th className="pb-1.5">Supplier</th>
                                <th className="pb-1.5 text-right">Commission<FieldHelp text="Flat commission payable to the collection point for this order — shown once per order even when it spans multiple suppliers." /></th>
                              </tr>
                            </thead>
                            <tbody>
                              {L.orders.map((od) => od.suppliers.map((sup, i) => (
                                <tr key={od.order_number + i} className="border-t border-border/50 font-body text-sm">
                                  <td className="py-1.5 text-foreground">{i === 0 ? od.order_number : ''}</td>
                                  <td className="py-1.5 text-foreground/70">{sup}</td>
                                  <td className="py-1.5 text-right text-teal font-medium">{i === 0 ? money(od.commission) : ''}</td>
                                </tr>
                              )))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="font-body text-xs text-foreground/40 mt-3">Commission is a flat fee per paid order to the collection point — shown once per order even when the order spans multiple suppliers.</p>
            </div>
          )}

          {/* Drill-down (paid orders transaction list) */}
          {isSales && (
          <div className="rounded-2xl bg-card border border-border p-6 overflow-x-auto">
            <div className="flex items-center justify-between mb-4 gap-3">
              <h2 className="font-heading text-lg font-medium text-foreground">Order Detail</h2>
              <Button
                data-testid="orderdetail-export-btn"
                onClick={() => handleExport('orders')}
                disabled={exporting || orders.length === 0}
                variant="outline"
                size="sm"
                className="rounded-full font-body text-xs gap-1.5 shrink-0"
              >
                <Download className="h-3.5 w-3.5" /> {exportingSection === 'orders' ? 'Exporting…' : 'Export'}
              </Button>
            </div>
            {orders.length === 0 ? (
              <p className="font-body text-sm text-foreground/50 text-center py-8">No paid orders for this selection</p>
            ) : (
              <table className="w-full" data-testid="report-orders-table">
                <thead>
                  <tr className="border-b border-border text-left font-body text-xs uppercase tracking-wider text-foreground/60">
                    <th className="pb-3">Order #</th>
                    <th className="pb-3">Date</th>
                    <th className="pb-3">Customer</th>
                    <th className="pb-3">Collection Location</th>
                    <th className="pb-3 text-right">Sales Price<FieldHelp text="Goods selling price × quantity for this order (excludes courier & collection fees)." /></th>
                    <th className="pb-3 text-right">Courier Fee<FieldHelp text="Delivery / courier charged on this order." /></th>
                    <th className="pb-3 text-right">Collection Fee<FieldHelp text="Collection-point fee charged on this order." /></th>
                    <th className="pb-3 text-right">Total Income<FieldHelp text="Sales Price + Courier Fee + Collection Fee." /></th>
                    <th className="pb-3 text-right">Cost Price<FieldHelp text="Supplier cost price × quantity for this order's items." /></th>
                    <th className="pb-3 text-right">Cost Ex VAT<FieldHelp text="VAT-exclusive cost of this order's items — VAT stripped for VAT-vendor suppliers." /></th>
                    <th className="pb-3 text-right">Commission<FieldHelp text="Collection commission payable for this order (flat fee to the collection point)." /></th>
                    <th className="pb-3 text-right">Gross Profit<FieldHelp text="Total Income − Cost Price − Collection Commission." /></th>
                    <th className="pb-3 text-center">Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => (
                    <tr
                      key={o.order_id}
                      data-testid={`report-order-row-${o.order_id}`}
                      onClick={() => openDrill(o.order_id)}
                      className="border-b border-border/50 last:border-0 font-body text-sm hover:bg-muted/40 cursor-pointer"
                    >
                      <td className="py-2.5 text-foreground font-medium underline decoration-dotted decoration-foreground/30">{o.order_number}</td>
                      <td className="py-2.5 text-foreground/60 text-xs">{(o.date || '').slice(0, 10)}</td>
                      <td className="py-2.5 text-foreground/70">{o.customer || '—'}</td>
                      <td className="py-2.5 text-foreground/70 text-xs">{o.collection_location || '—'}</td>
                      <td className="py-2.5 text-right text-foreground">{money(o.sales_price)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(o.courier_fee)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(o.collection_fee)}</td>
                      <td className="py-2.5 text-right text-foreground">{money(o.total_income)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(o.cost_price)}</td>
                      <td className="py-2.5 text-right text-foreground/70">{money(o.cost_price_ex_vat ?? o.cost_price)}</td>
                      <td className="py-2.5 text-right text-orange-400">{money(o.collection_commission)}</td>
                      <td className="py-2.5 text-right font-semibold text-teal">{money(o.gross_profit)}</td>
                      <td className="py-2.5 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          o.payment_type === 'Card' ? 'bg-teal/15 text-teal' : 'bg-gold/15 text-gold'
                        }`}>
                          <CreditCard className="h-3 w-3" /> {o.payment_type}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          )}
        </>
      )}

      {/* Drill-down: per-order line-item profit breakdown */}
      <Dialog open={!!drillOrder} onOpenChange={(o) => { if (!o) setDrillOrder(null); }}>
        <DialogContent className="max-w-5xl bg-card border-border max-h-[88vh] overflow-y-auto" data-testid="report-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-light text-foreground">
              {drillLoading || drillOrder?.loading ? 'Loading order…' : `Order ${drillOrder?.order_number || ''}`}
            </DialogTitle>
          </DialogHeader>

          {drillLoading || drillOrder?.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-teal" />
            </div>
          ) : drillOrder ? (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-x-6 gap-y-1 font-body text-sm text-foreground/70">
                <span><span className="text-foreground/50">Customer:</span> {drillOrder.customer || '—'}</span>
                <span><span className="text-foreground/50">Date:</span> {(drillOrder.date || '').slice(0, 10)}</span>
                <span><span className="text-foreground/50">Payment:</span> {drillOrder.payment_type}</span>
                <span data-testid="drill-payment-status"><span className="text-foreground/50">Payment status:</span> <span className={drillOrder.payment_status === 'paid' ? 'text-teal capitalize' : 'text-orange-400 capitalize'}>{drillOrder.payment_status || '—'}</span></span>
                <span><span className="text-foreground/50">Collected at:</span> {drillOrder.collection_location || '—'}</span>
              </div>

              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full" data-testid="drill-items-table">
                  <thead>
                    <tr className="bg-muted/40 text-left font-body text-[11px] uppercase tracking-wider text-foreground/60">
                      <th className="p-2.5">Product</th>
                      <th className="p-2.5">Supplier</th>
                      <th className="p-2.5 text-center">Qty</th>
                      <th className="p-2.5 text-right">Unit Price</th>
                      <th className="p-2.5 text-right">Unit Cost</th>
                      <th className="p-2.5 text-right">Unit Cost Ex VAT</th>
                      <th className="p-2.5 text-right">Line Total</th>
                      <th className="p-2.5 text-right">Delivery</th>
                      <th className="p-2.5 text-right">Collection</th>
                      <th className="p-2.5 text-right">Markup</th>
                      <th className="p-2.5">Purchase Order</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(drillOrder.items || []).map((it, i) => (
                      <tr key={i} className={`border-t border-border/50 font-body text-sm ${it.refunded ? 'opacity-50 line-through' : ''}`}>
                        <td className="p-2.5 text-foreground">{it.product_name}{it.refunded ? ' (refunded)' : ''}</td>
                        <td className="p-2.5 text-foreground/70">{it.supplier_name || '—'}</td>
                        <td className="p-2.5 text-center text-foreground/70">{it.quantity}</td>
                        <td className="p-2.5 text-right text-foreground/70">{money(it.unit_price)}</td>
                        <td className="p-2.5 text-right text-foreground/70">{money(it.unit_cost)}</td>
                        <td className="p-2.5 text-right text-foreground/70">{money(it.unit_cost_ex_vat ?? it.unit_cost)}</td>
                        <td className="p-2.5 text-right text-foreground">{money(it.line_total)}</td>
                        <td className="p-2.5 text-right text-foreground/70" data-testid={`drill-item-delivery-${i}`}>{money(it.delivery_alloc)}</td>
                        <td className="p-2.5 text-right text-foreground/70" data-testid={`drill-item-collection-${i}`}>{money(it.collection_alloc)}</td>
                        <td className="p-2.5 text-right font-semibold text-teal" data-testid={`drill-item-markup-${i}`}>{money(it.markup)}</td>
                        <td className="p-2.5 text-foreground/70" data-testid={`drill-item-po-${i}`}>
                          {(it.purchase_orders && it.purchase_orders.length > 0) ? (
                            <div className="flex flex-col gap-0.5">
                              {it.purchase_orders.map((po, pi) => (
                                <span key={pi} className="whitespace-nowrap">
                                  {po.po_number} <span className="text-foreground/40">· {po.status}</span>
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-orange-400">Pending PO</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Customer charges — mirrors the Order Detail modal */}
                <div className="rounded-xl bg-muted/30 border border-border p-4 space-y-1.5 font-body text-sm" data-testid="drill-customer-charges">
                  <p className="font-heading text-xs uppercase tracking-wider text-foreground/50 mb-1">Customer Charges</p>
                  <div className="flex justify-between text-foreground/70"><span>Items subtotal</span><span>{money(drillOrder.items_subtotal)}</span></div>
                  {(drillOrder.delivery_lines || []).map((d, i) => (
                    <div key={i} className="flex justify-between text-foreground/60 text-xs"><span>Delivery · {d.supplier_name}</span><span>{money(d.amount)}</span></div>
                  ))}
                  {(!drillOrder.delivery_lines || drillOrder.delivery_lines.length === 0) && drillOrder.courier_fee > 0 && (
                    <div className="flex justify-between text-foreground/70"><span>Delivery fee</span><span>{money(drillOrder.courier_fee)}</span></div>
                  )}
                  {drillOrder.collection_fee > 0 && (
                    <div className="flex justify-between text-foreground/70"><span>Collection fee</span><span>{money(drillOrder.collection_fee)}</span></div>
                  )}
                  <div className="flex justify-between text-foreground font-semibold pt-1.5 border-t border-border"><span>Order Total</span><span>{money(drillOrder.order_total)}</span></div>
                </div>

                {/* Profitability */}
                <div className="rounded-xl bg-muted/30 border border-border p-4 space-y-1.5 font-body text-sm">
                  <p className="font-heading text-xs uppercase tracking-wider text-foreground/50 mb-1">Profitability</p>
                  <div className="flex justify-between text-foreground/70"><span>Sales (goods)</span><span>{money(drillOrder.sales_price)}</span></div>
                  <div className="flex justify-between text-foreground/70"><span>Courier fee</span><span>{money(drillOrder.courier_fee)}</span></div>
                  <div className="flex justify-between text-foreground/70"><span>Collection fee</span><span>{money(drillOrder.collection_fee)}</span></div>
                  <div className="flex justify-between text-foreground font-medium pt-1.5 border-t border-border"><span>Total Income</span><span>{money(drillOrder.total_income)}</span></div>
                  <div className="flex justify-between text-foreground/70"><span>Cost of goods</span><span>−{money(drillOrder.cost_price)}</span></div>
                  <div className="flex justify-between text-foreground/50 text-xs"><span>Cost of goods (ex VAT)</span><span>−{money(drillOrder.cost_price_ex_vat ?? drillOrder.cost_price)}</span></div>
                  <div className="flex justify-between text-orange-400"><span>Collection commission</span><span>−{money(drillOrder.collection_commission)}</span></div>
                  <div className="flex justify-between text-teal font-bold text-base pt-1.5 border-t border-border"><span>Gross Profit</span><span>{money(drillOrder.gross_profit)}</span></div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
